import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { numStr, parseNum } from "@/lib/format";
import { shoppingListText } from "@/lib/shoppingList";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { Button, Card, ErrorState, Field, Input, Loading, Page, PageHeader, StickyFooter } from "@/components/kit";
import ProductPicker from "@/components/shopping/ProductPicker";
import SelectedItems from "@/components/shopping/SelectedItems";
import SharePanel from "@/components/shopping/SharePanel";

const toLocal = (i) => ({ ...i, qty: numStr(i.qty) });

function useListForm(id) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ["shopping-list", id], queryFn: () => api(`/shopping-lists/${id}`), enabled: !!id, retry: false });
  const [f, setF] = useState({ name: "", note: "" });
  const [items, setItems] = useState([]);
  useEffect(() => { if (data) { setF({ name: data.name, note: data.note || "" }); setItems(data.items.map(toLocal)); } }, [data]);
  const body = () => ({ ...f, items: items.filter((i) => parseNum(i.qty) > 0).map((i) => ({ product_id: i.product_id, qty: parseNum(i.qty) })) });
  const save = useMutation({
    mutationFn: () => api(id ? `/shopping-lists/${id}` : "/shopping-lists", { method: id ? "PUT" : "POST", body: body() }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["shopping-lists"] });
      qc.setQueryData(["shopping-list", d.id], d);
      toast.success(id ? "Lista atualizada" : "Lista criada");
      if (!id) nav(`/shopping-lists/${d.id}`, { replace: true });
    },
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: () => api(`/shopping-lists/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["shopping-lists"] }); toast.success("Lista excluída"); nav("/shopping-lists", { replace: true }); },
    onError: (e) => toast.error(e.message),
  });
  const add = (p) => setItems((xs) => [...xs, { product_id: p.id, product_name: p.name, unit: p.unit, unit_cost: p.unit_cost, category_name: p.category_name, qty: "1" }]);
  const setQty = (pid, v) => setItems((xs) => xs.map((i) => (i.product_id === pid ? { ...i, qty: v } : i)));
  const remove = (pid) => setItems((xs) => xs.filter((i) => i.product_id !== pid));
  return { f, setF, items, add, setQty, remove, save, del, loading: !!id && isLoading, error: !!id && isError ? error.message : null };
}

export default function ShoppingListForm() {
  const { id } = useParams();
  const { isMgmt } = useAuth();
  const { unit } = useUnit();
  const form = useListForm(id);
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: () => api("/products") });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories") });
  const selectedIds = useMemo(() => new Set(form.items.map((i) => i.product_id)), [form.items]);
  const valid = form.items.filter((i) => parseNum(i.qty) > 0);
  const text = shoppingListText({ name: form.f.name || "Sem nome", note: form.f.note, items: valid.map((i) => ({ ...i, qty: parseNum(i.qty) })) }, unit?.name);
  const submit = () => (form.f.name.trim() ? form.save.mutate() : toast.error("Informe o nome da lista"));
  if (form.loading) return <Loading />;
  if (form.error) return <Page><PageHeader title="Lista de compras" back="/shopping-lists" /><ErrorState message={form.error} /></Page>;
  return (
    <Page>
      <PageHeader title={id ? "Editar lista" : "Nova lista de compras"} back="/shopping-lists"
        right={id && <button className="icon-btn" title="Excluir" onClick={() => window.confirm("Excluir esta lista?") && form.del.mutate()} data-testid="delete-shopping-list-button"><Trash2 size={16} /></button>} />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
        <div className="flex flex-col gap-4 min-w-0">
          <Card className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome da lista"><Input placeholder="Ex.: Compras semanais" maxLength={80} value={form.f.name} onChange={(e) => form.setF({ ...form.f, name: e.target.value })} data-testid="shopping-list-name-input" /></Field>
            <Field label="Observação (opcional)"><Input placeholder="Ex.: entregar até sexta" maxLength={300} value={form.f.note} onChange={(e) => form.setF({ ...form.f, note: e.target.value })} data-testid="shopping-list-note-input" /></Field>
          </Card>
          <SelectedItems items={form.items} onQty={form.setQty} onRemove={form.remove} showCost={isMgmt} />
          <ProductPicker products={products} categories={categories} selectedIds={selectedIds} onAdd={form.add} showCost={isMgmt} />
        </div>
        <div className="lg:sticky lg:top-6 min-w-0">
          <SharePanel text={text} title={form.f.name} disabled={!valid.length} />
        </div>
      </div>
      <StickyFooter><Button icon={Save} loading={form.save.isPending} onClick={submit} data-testid="save-shopping-list-button">Salvar lista</Button></StickyFooter>
    </Page>
  );
}
